import React from "react";
import {
  List,
  SimpleList,
  Datagrid,
  TextField,
  NumberField,
  Edit,
  Pagination,
  AutocompleteArrayInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useUnselectAll,
  useRecordContext,
  useListContext,
  BulkActionsToolbar,
} from "react-admin";
import useMediaQuery from "@mui/material/useMediaQuery";

import { orderItemStatuses, orderItemChannels } from "./orders";
import ListActions from "./components/OrderItem/ListActions";
import OrderItemEditForm from "./components/OrderItem/OrderItemEditForm";
import CustomPickerInput from "./components/OrderItem/CustomPickerInput";
import BulkUpdateTagsButton from "./components/OrderItem/BulkUpdateTagsButton";
import BulkRemovePrefix from "./components/OrderItem/BulkRemovePrefix";
import { getOrderItemSearchStartDate } from "./helpers/methods";
import { withDisabledIfCancelled } from "./helpers/withDisabled";
import BulkUpdateOrderItemButton from "./components/BulkUpdateOrderItem";
import { ORDER_ITEM_CANCELLED } from "./components/Constant";
import CustomChipArrayField from "./components/CustomChipArrayField";
import CustomDescriptionSearch from "./components/OrderItem/CustomDescriptionSearch";
import CustomItemDescriptionWithImage from "./Order/CustomItemDescriptionWithImage";
import TextLinkField from "./components/Common/TextLinkField";
import CustomKeywordSearch from "./components/OrderItem/CustomKeywordSearch";
import CustomDateField from "./components/Common/CustomDateField";
import UserActionByResource from "./components/History/UserActionByResource";
import { useProfileLocale } from "./components/Common/locale";
import BulkPrintLabelButton from "./components/OrderItem/BulkPrintLabel";

const OrderItemTextLinkField = withDisabledIfCancelled(TextLinkField);

const OrderItemTextField = withDisabledIfCancelled(TextField);
const OrderItemNumberField = withDisabledIfCancelled(NumberField);
const OrderItemDateField = withDisabledIfCancelled(CustomDateField);

const dateInputOption = {
  disableFuture: true,
};

const OrderItemBulkActionButtons = () => {
  const props = useListContext();
  return (
    <BulkActionsToolbar selectAllButton={false}>
      <BulkUpdateOrderItemButton label="update orderItem" {...props} />
      <BulkUpdateTagsButton {...props} />
      <BulkRemovePrefix {...props} />
      <BulkPrintLabelButton {...props} />
    </BulkActionsToolbar>
  );
};

export const OrderItemEdit = () => {
  const record = useRecordContext();

  return (
    <Edit {...record}>
      <OrderItemEditForm>
        <TextField
          record={record}
          source="order.customerName"
          label="Customer name"
        />
        <CustomDateField source="createdAt" showTime />
        <CustomDateField source="updatedAt" showTime />
      </OrderItemEditForm>
      <UserActionByResource />
    </Edit>
  );
};

const orderItemFilter = [
  <CustomKeywordSearch
    alwaysOn
    label="Search by keyword"
    source="keyword_in"
  />,
  <CustomDescriptionSearch
    alwaysOn
    label="Item"
    source="descriptionSearch_containss"
  />,
  <ReferenceArrayInput
    label="Tags"
    source="tags"
    reference="tags"
    perPage={100}
  >
    <SelectArrayInput optionText="label" />
  </ReferenceArrayInput>,
  <AutocompleteArrayInput
    source="status_in"
    label={`Status`}
    choices={orderItemStatuses}
    alwaysOn
    //allowEmpty={false}
  />,
  <AutocompleteArrayInput
    source="channel_in"
    label={`Channel`}
    choices={orderItemChannels}
    //allowEmpty={false}
  />,
  <CustomPickerInput
    source="createdAt_gte"
    label="CreatedAt start"
    options={dateInputOption}
    alwaysOn
  />,
  <CustomPickerInput
    source="createdAt_lte"
    label="CreatedAt end"
    options={dateInputOption}
  />,
  <CustomPickerInput
    source="updatedAt_gte"
    label="UpdatedAt start"
    options={dateInputOption}
  />,
  <CustomPickerInput
    source="updatedAt_lte"
    label="UpdatedAt end"
    options={dateInputOption}
  />,
];

const tinyFont = { fontSize: "10px" };

export const OrderItemList = (props) => {
  const unselectAll = useUnselectAll("orderItems");
  const locale = useProfileLocale();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  React.useEffect(() => {
    return () => unselectAll();
  }, [unselectAll]);

  return (
    <List
      debounce={2000}
      {...props}
      actions={<ListActions />}
      hasCreate={false}
      filterDefaultValues={{
        createdAt_gte: getOrderItemSearchStartDate().toISOString(),
      }}
      perPage={25}
      filters={orderItemFilter}
      pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 200]} />}
      sort={{ field: "createdAt", order: "DESC" }}
      emptyWhileLoading
    >
      {isSmall ? (
        <SimpleList
          // linkType="show"
          primaryText={(record) => record.description}
          secondaryText={(record) =>
            `${record.order ? record.order.customerName : ""}; price: ${
              record.price
            }; ${record.status}`
          }
          tertiaryText={(record) => (
            <>
              <span style={tinyFont}>createdAt</span>
              <br />{" "}
              {`${new Date(record.createdAt).toLocaleDateString(locale)}`}
            </>
          )}
        />
      ) : (
        <Datagrid
          rowClick="edit"
          isRowSelectable={({ status }) => status !== ORDER_ITEM_CANCELLED}
          bulkActionsToolbar={<OrderItemBulkActionButtons />}
        >
          <CustomItemDescriptionWithImage source="description" />
          <OrderItemTextLinkField
            source="keyword.key"
            resourceName="keywords"
            resourceKeyId="keyword._id"
            sortable={false}
          />
          <OrderItemTextField source="order.customerName" sortable={false} />
          <OrderItemTextField source="channel" />
          <OrderItemTextField source="status" />
          <OrderItemNumberField source="price" />
          <OrderItemTextField source="order.status" sortable={false} />
          <CustomChipArrayField source="tags" />
          <OrderItemDateField source="createdAt" showTime />
          <OrderItemDateField source="updatedAt" showTime />
        </Datagrid>
      )}
    </List>
  );
};
